import { z } from 'zod';

import { API_PATHS } from '../../../lib/apiRoutes';
import { apiRequest } from '../../../lib/api';
import { buildIdempotencyKey } from '../../../lib/idempotency';
import { validateWithSchema } from '../../shared/utils/apiValidation.util';
import { normalizeCartLines } from '../utils/cartNormalization';

const cartResponseSchema = z
  .object({
    items: z.array(z.object({
      id: z.string().trim().min(1).optional(),
      productId: z.string().trim().min(1).optional(),
      quantity: z.number().int().positive().optional(),
      temperaturePreference: z.enum(['chilled', 'ambient', 'cold', 'either']).optional()
    })).default([])
  })
  .passthrough();

const idempotencyKeySchema = z.string().trim().min(8).max(128);
const resolveIdempotencyKey = (scope: string, provided?: string) => idempotencyKeySchema.parse(provided?.trim() || buildIdempotencyKey(scope));

const returnUpcCountSchema = z
  .union([z.number(), z.string()])
  .transform((value) => Number(value))
  .refine((value) => Number.isFinite(value), 'Return UPC quantity must be numeric')
  .transform((value) => Math.floor(value))
  .refine((value) => value >= 0, 'Return UPC quantity must be non-negative');

const returnUpcEntrySchema = z
  .object({
    upc: z.string().trim().min(1),
    quantity: returnUpcCountSchema.optional(),
    count: returnUpcCountSchema.optional()
  })
  .transform((entry) => ({
    upc: entry.upc,
    quantity: entry.quantity ?? entry.count ?? 0
  }));

const returnCreditPreviewSchema = z
  .object({
    containerCount: z.number().int().nonnegative(),
    grossCredit: z.number().nonnegative(),
    handlingFee: z.number().nonnegative(),
    glassFee: z.number().nonnegative(),
    estimatedReturnCredit: z.number().nonnegative()
  })
  .passthrough();

const returnUpcsSchema = z
  .object({
    upcs: z.array(returnUpcEntrySchema).default([]),
    eligibilityCache: z.record(z.unknown()).optional(),
    returnCreditPreview: returnCreditPreviewSchema.nullable().optional()
  })
  .passthrough();


const checkoutQuoteResponseSchema = z
  .object({
    data: z
      .object({
        total: z.union([z.number(), z.string()]).optional(),
        distanceMiles: z.union([z.number(), z.string()]).optional(),
        fees: z
          .object({
            routeFee: z.union([z.number(), z.string()]).optional(),
            distanceFee: z.union([z.number(), z.string()]).optional(),
            distanceMiles: z.union([z.number(), z.string()]).optional(),
            largeOrderFee: z.union([z.number(), z.string()]).optional(),
            heavyItemFee: z.union([z.number(), z.string()]).optional()
          })
          .passthrough()
          .optional(),
        tierBenefits: z
          .object({
            allowedReturnPayoutMethods: z.array(z.enum(['CREDIT', 'CASH'])).optional(),
            creditsCanCoverDelivery: z.boolean().optional()
          })
          .passthrough()
          .optional()
      })
      .passthrough()
      .optional()
  })
  .passthrough();

const checkoutPreviewResponseSchema = z
  .object({
    data: z
      .object({
        fulfillment: z.unknown().optional(),
        stores: z.array(z.unknown()).optional(),
        primaryStoreRecommendation: z.unknown().nullable().optional(),
        fees: z.unknown().optional(),
        authorizedAmount: z.number().optional(),
        authorizedAmountCents: z.number().optional(),
        authorizationBufferCents: z.number().optional(),
        authorizationBufferMultiplier: z.number().optional(),
        authorizationDisclosure: z.string().optional(),
        returnCreditPreview: returnCreditPreviewSchema.nullable().optional(),
        tierBenefits: z
          .object({
            allowedReturnPayoutMethods: z.array(z.enum(['CREDIT', 'CASH'])).optional(),
            creditsCanCoverDelivery: z.boolean().optional()
          })
          .passthrough()
          .optional()
      })
      .passthrough()
      .optional(),
    authorizedAmount: z.number().optional(),
    authorizedAmountCents: z.number().optional(),
    authorizationBufferCents: z.number().optional(),
    authorizationBufferMultiplier: z.number().optional(),
    authorizationDisclosure: z.string().optional()
  })
  .passthrough();

type CheckoutQuoteResponse = z.infer<typeof checkoutQuoteResponseSchema>;
type CheckoutPreviewResponse = z.infer<typeof checkoutPreviewResponseSchema>;

const quoteCheckoutPayloadSchema = z.object({
  isStandaloneReturn: z.boolean().optional(),
  returnUpcs: z.array(z.string()).optional(),
  cartItems: z.array(
    z.object({
      productId: z.string().trim().min(1),
      quantity: z.number().int().positive(),
      temperaturePreference: z.enum(['chilled', 'ambient', 'cold', 'either']).optional()
    })
  ).optional(),
  deliveryAddress: z.unknown()
}).passthrough();


type CartLine = {
  id: string;
  productId: string;
  quantity: number;
  temperaturePreference?: 'chilled' | 'ambient' | 'cold' | 'either';
};

export type CheckoutDraftPayload = {
  shippingAddress?: string;
  acceptedPolicies?: boolean;
  deliveryInstructions?: string;
  savedAddressId?: string;
  savedPaymentMethodId?: string;
  requestedDeliveryWindowId?: string;
  tipCents?: number;
  applyWalletBalance?: boolean;
  promoCode?: string;
  useCashPayout?: boolean;
};

type CartResponse = {
  items: CartLine[];
  cartVersion: number;
  checkoutDraft?: CheckoutDraftPayload | null;
};
type ReturnUpcsResponse = {
  upcs: Array<{ upc: string; quantity: number }>;
  eligibilityCache?: Record<string, unknown>;
  returnCreditPreview?: { estimatedReturnCredit: number } | null;
};

export const cartApi = {
  get: async (): Promise<CartResponse> => {
    const raw = await apiRequest<unknown>(API_PATHS.cart.root);
    const envelope = raw && typeof raw === 'object' ? (raw as { data?: unknown }) : null;
    const payload = envelope?.data && typeof envelope.data === 'object' ? envelope.data : raw;
    const data = validateWithSchema(cartResponseSchema, payload, API_PATHS.cart.root) as {
      items: Array<{ id?: string; productId?: string; quantity?: number }>;
      cartVersion?: unknown;
      checkoutDraft?: CheckoutDraftPayload | null;
    };
    return {
      items: normalizeCartLines(data.items),
      cartVersion: Number.isFinite(Number(data.cartVersion)) ? Number(data.cartVersion) : 0,
      checkoutDraft: data.checkoutDraft ?? null
    };
  },
  patchCheckoutDraft: async (
    draft: CheckoutDraftPayload,
    options?: { keepalive?: boolean; cartVersion?: number }
  ) => {
    const response = await apiRequest<{
      data?: { checkoutDraft?: CheckoutDraftPayload; cartVersion?: number };
    }>(API_PATHS.cart.checkoutDraft, {
      method: 'PATCH',
      idempotencyKey: resolveIdempotencyKey('cartCheckoutDraft'),
      requireIdempotencyKey: true,
      body: JSON.stringify({
        ...draft,
        ...(Number.isInteger(options?.cartVersion) ? { cartVersion: options?.cartVersion } : {})
      }),
      keepalive: options?.keepalive === true
    });
    return {
      checkoutDraft: response?.data?.checkoutDraft ?? draft,
      cartVersion: Number(response?.data?.cartVersion ?? options?.cartVersion ?? 0)
    };
  },
  put: async (
    items: Array<{ id?: string; productId?: string; quantity: number }>,
    idempotencyKey?: string,
    cartVersion?: number,
    options?: { keepalive?: boolean }
  ): Promise<{ cartVersion: number }> => {
    const normalized = normalizeCartLines(items);
    const response = await apiRequest<{ data?: { cartVersion?: number } }>(API_PATHS.cart.root, {
      method: 'PUT',
      idempotencyKey: resolveIdempotencyKey('cartPut', idempotencyKey),
      requireIdempotencyKey: true,
      keepalive: options?.keepalive === true,
      body: JSON.stringify({
        items: normalized.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          temperaturePreference: item.temperaturePreference
        })),
        ...(Number.isInteger(cartVersion) ? { cartVersion } : {})
      })
    });
    return { cartVersion: Number(response?.data?.cartVersion ?? 0) };
  },
  clear: async (cartVersion?: number): Promise<{ cartVersion: number }> => {
    const query =
      Number.isInteger(cartVersion) ? `?cartVersion=${encodeURIComponent(String(cartVersion))}` : '';
    const response = await apiRequest<{ data?: { cartVersion?: number; items?: unknown[] } }>(
      `${API_PATHS.cart.root}${query}`,
      {
        method: 'DELETE',
        idempotencyKey: resolveIdempotencyKey('cartClear'),
        requireIdempotencyKey: true
      }
    );
    const nextVersion = Number(response?.data?.cartVersion);
    if (!Number.isFinite(nextVersion)) {
      throw new Error('Cart clear was not confirmed by the server.');
    }
    return { cartVersion: nextVersion };
  },
  getReturnUpcs: async (): Promise<ReturnUpcsResponse> => {
    const raw = await apiRequest<unknown>(API_PATHS.cart.returnUpcs);
    const envelope = raw && typeof raw === 'object' ? (raw as { data?: unknown }) : null;
    const payload = envelope?.data && typeof envelope.data === 'object' ? envelope.data : raw;
    return validateWithSchema(returnUpcsSchema, payload, API_PATHS.cart.returnUpcs) as ReturnUpcsResponse;
  },
  putReturnUpcs: (payload: Record<string, unknown>, idempotencyKey?: string) => {
    const parsed = validateWithSchema(
      z.object({
        upcs: z.array(returnUpcEntrySchema),
        eligibilityCache: z.record(z.unknown()).optional()
      }),
      payload,
      API_PATHS.cart.returnUpcs
    ) as ReturnUpcsResponse;

    const canonicalPayload = {
      upcs: parsed.upcs.map((entry) => ({
        upc: entry.upc,
        quantity: entry.quantity,
        count: entry.quantity
      })),
      eligibilityCache: parsed.eligibilityCache
    };

    return apiRequest<void>(API_PATHS.cart.returnUpcs, {
      method: 'PUT',
      idempotencyKey: resolveIdempotencyKey('cartReturnUpcs', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(canonicalPayload)
    });
  },
  clearReturnUpcs: () =>
    apiRequest<void>(API_PATHS.cart.returnUpcs, {
      method: 'DELETE',
      idempotencyKey: resolveIdempotencyKey('cartReturnUpcsClear'),
      requireIdempotencyKey: true
    }),
  quoteCheckout: async (payload: Record<string, unknown>, idempotencyKeyOrSignal?: string | AbortSignal, signal?: AbortSignal): Promise<CheckoutQuoteResponse> => {
    const resolvedSignal = idempotencyKeyOrSignal instanceof AbortSignal ? idempotencyKeyOrSignal : signal;
    const resolvedKey = typeof idempotencyKeyOrSignal === 'string' ? idempotencyKeyOrSignal : undefined;
    const response = await apiRequest<unknown>(API_PATHS.checkout.quote, {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('checkoutQuote', resolvedKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(validateWithSchema(quoteCheckoutPayloadSchema, payload, API_PATHS.checkout.quote)),
      signal: resolvedSignal
    });

    return validateWithSchema(checkoutQuoteResponseSchema, response, API_PATHS.checkout.quote) as CheckoutQuoteResponse;
  },
  getCheckoutPreview: async (payload: Record<string, unknown>, idempotencyKeyOrSignal?: string | AbortSignal, signal?: AbortSignal): Promise<CheckoutPreviewResponse> => {
    const resolvedSignal = idempotencyKeyOrSignal instanceof AbortSignal ? idempotencyKeyOrSignal : signal;
    const resolvedKey = typeof idempotencyKeyOrSignal === 'string' ? idempotencyKeyOrSignal : undefined;
    const response = await apiRequest<unknown>(API_PATHS.checkout.preview, {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('checkoutPreview', resolvedKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(validateWithSchema(quoteCheckoutPayloadSchema, payload, API_PATHS.checkout.preview)),
      signal: resolvedSignal
    });

    return validateWithSchema(checkoutPreviewResponseSchema, response, API_PATHS.checkout.preview) as CheckoutPreviewResponse;
  },
  optimize: async () => {
    return apiRequest<{
      optimizedCart?: {
        items: Array<{ productId: string; quantity: number }>;
        subtotal?: number;
      };
    }>(API_PATHS.checkout.preview, { method: 'POST' });
  }
};
